import React from 'react';
import {
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderItemSubsequentPageTitleUI,
  arrayBuilderYesNoSchema,
  arrayBuilderYesNoUI,
  fullNameSchema,
  fullNameUI,
  currentOrPastDateUI,
  currentOrPastDateSchema,
  textUI,
  selectUI,
  radioUI,
  radioSchema,
  checkboxUI,
  checkboxSchema,
  yesNoUI,
  yesNoSchema,
} from 'platform/forms-system/src/js/web-component-patterns';
import { arrayBuilderPages } from 'platform/forms-system/src/js/patterns/array-builder';
import { replaceStrValues } from 'platform/shared/health-apps';
import titleCase from 'platform/utilities/data/titleCase';
import { handleSpouseMaxMarriagesAlert } from '../../../components/FormAlerts';
import {
  STATE_VALUES,
  STATE_NAMES,
  COUNTRY_VALUES,
  COUNTRY_NAMES,
  previousMarriageEndOptions,
} from '../../../utils/labels';
import { customAddressSchema, customTextSchema } from '../../definitions';
import { validations } from '../../validations';
import content from '../../../utils/content.json';

// Show previous marriages pages ONLY if user answered YES to hadPreviousMarriages
// Ansering NO skips all previous marriage flows and jumps to Dependents
const showClaimantMarriagePage = formData =>
  formData.claimantRelationship === 'SURVIVING_SPOUSE' &&
  formData?.['view:hasAnyPreviousMarriages'] === true &&
  formData.hadPreviousMarriages?.claimant === true;

/** @type {ArrayBuilderOptions} */
// arrayPath is spouseMarriages because it's the spouse's previous marriages
export const options = {
  arrayPath: 'spouseMarriages',
  nounSingular: content['household-previous-marriages-noun-singular'],
  nounPlural: content['household-previous-marriages-noun-plural'],
  required: false,
  isItemIncomplete: item =>
    !item?.spouseFullName?.first ||
    !item?.spouseFullName?.last ||
    !item?.dateOfMarriage ||
    !item?.locationOfMarriage?.city ||
    (!item?.marriedOutsideUs && !item?.locationOfMarriage?.state) ||
    (item?.marriedOutsideUs && !item?.locationOfMarriage?.otherCountry) ||
    !item?.reasonForSeparation ||
    (item?.reasonForSeparation === 'OTHER' && !item?.separationExplanation),
  maxItems: 2,
  text: {
    cancelAddTitle: replaceStrValues(
      content['array-cancel-add-title'],
      `${content['household-previous-marriages-noun-singular']}?`,
    ),
    cancelEditTitle: replaceStrValues(
      content['array-cancel-edit-title'],
      `${content['household-previous-marriages-noun-singular']}?`,
    ),
    cancelAddDescription: replaceStrValues(
      content['household-previous-marriages-cancel-add-description'],
      content['household-previous-marriages-previous-marriage-suffix'],
    ),
    cancelAddReviewDescription: replaceStrValues(
      content['household-previous-marriages-cancel-add-description'],
      content['household-previous-marriages-previous-marriage-suffix'],
    ),
    cancelEditDescription:
      content['household-previous-marriages-cancel-edit-description'],
    cancelEditReviewDescription:
      content['household-previous-marriages-cancel-edit-description'],
    cancelAddYes: content['array-cancel-add-yes'],
    cancelAddNo: content['array-cancel-add-no'],
    cancelEditYes: content['array-cancel-edit-yes'],
    cancelEditNo: content['array-cancel-edit-no'],
    deleteDescription: replaceStrValues(
      content['household-previous-marriages-delete-description'],
      [
        content.your,
        content['household-previous-marriages-previous-marriage-suffix'],
      ],
    ),
    deleteNeedAtLeastOneDescription: content['array-empty-hint'],
    deleteNo: content['array-delete-no'],
    deleteTitle: replaceStrValues(
      content['array-delete-title'],
      `${content['household-previous-marriages-noun-singular']}?`,
    ),
    deleteYes: content['array-delete-yes'],
    alertMaxItems: handleSpouseMaxMarriagesAlert,
    getItemName: item => {
      const name = item?.spouseFullName;
      if (!name?.first && !name?.last) return '';

      const parts = [];
      if (name?.first) parts.push(name.first);
      if (name?.middle) parts.push(name.middle);
      if (name?.last) parts.push(name.last);
      if (name?.suffix) parts.push(name.suffix);

      return parts.join(' ');
    },
  },
};

function introDescription() {
  return (
    <div>
      <p>
        Next we'll ask you about your previous marriages before your marriage to
        the Veteran. You may add up to 2 marriages.
      </p>
      <p>
        <strong>Note:</strong> We usually don’t need to contact a previous
        spouse. In very rare cases where we need information from this person,
        we’ll contact you first.
      </p>
    </div>
  );
}

/** @returns {PageSchema} */
const introPage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: content['household-your-previous-marriages-intro-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
    }),
    'ui:description': introDescription,
  },
  schema: {
    type: 'object',
    properties: {},
  },
};

/** @returns {PageSchema} */
const summaryPage = {
  uiSchema: {
    'view:hasPreviousMarriages': arrayBuilderYesNoUI(
      options,
      {
        title: content['household-your-previous-marriages-summary-add'],
        hint: content['array-empty-hint'],
      },
      {
        title: content['household-your-previous-marriages-summary-add-another'],
        hint: content['array-empty-hint'],
      },
    ),
    spouseHasAdditionalMarriages: {
      ...yesNoUI({
        title: content['household-your-previous-marriages-summary-additional'],
      }),
      'ui:required': formData => formData?.spouseMarriages?.length === 2,
      'ui:options': {
        hideIf: formData =>
          !formData?.spouseMarriages || formData?.spouseMarriages?.length < 2,
      },
    },
  },
  schema: {
    type: 'object',
    required: ['view:hasPreviousMarriages'],
    properties: {
      'view:hasPreviousMarriages': arrayBuilderYesNoSchema,
      spouseHasAdditionalMarriages: yesNoSchema,
    },
  },
};

/** @returns {PageSchema} */
const previousMarriageItemPage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: content['household-your-previous-marriages-name-title'],
      nounSingular: options.nounSingular,
    }),
    spouseFullName: fullNameUI(),
  },
  schema: {
    type: 'object',
    properties: {
      spouseFullName: fullNameSchema,
    },
    required: ['spouseFullName'],
  },
};

/** @returns {PageSchema} */
const marriageDateAndLocationPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      replaceStrValues(
        content['household-previous-marriages-date-location-title'],
        content.you,
      ),
    ),
    dateOfMarriage: currentOrPastDateUI({
      title: content['household-previous-marriages-date-location-date'],
      monthSelect: false,
    }),
    marriedOutsideUs: checkboxUI({
      title: replaceStrValues(
        content['household-previous-marriages-date-location-outside-us'],
        content.i,
      ),
    }),
    locationOfMarriage: {
      city: {
        ...textUI(content['address-city']),
        'ui:errorMessages': {
          required: content['address-city-error'],
        },
      },
      state: {
        ...selectUI(content['address-state']),
        'ui:required': (formData, index) => {
          const item = formData?.spouseMarriages?.[index];
          const currentPageData = formData;
          return !(item?.marriedOutsideUs || currentPageData?.marriedOutsideUs);
        },
        'ui:options': {
          hideIf: (formData, index) => {
            const item = formData?.spouseMarriages?.[index];
            const currentPageData = formData;
            return item?.marriedOutsideUs || currentPageData?.marriedOutsideUs;
          },
          labels: STATE_VALUES.reduce((acc, value, idx) => {
            acc[value] = STATE_NAMES[idx];
            return acc;
          }, {}),
        },
        'ui:errorMessages': {
          required: content['address-state-error'],
        },
      },
      otherCountry: {
        ...selectUI(content['address-country']),
        'ui:required': (formData, index) => {
          const item = formData?.spouseMarriages?.[index];
          const currentPageData = formData;
          return item?.marriedOutsideUs || currentPageData?.marriedOutsideUs;
        },
        'ui:options': {
          hideIf: (formData, index) => {
            const item = formData?.spouseMarriages?.[index];
            const currentPageData = formData;
            return !(
              item?.marriedOutsideUs || currentPageData?.marriedOutsideUs
            );
          },
          labels: COUNTRY_VALUES.reduce((acc, value, idx) => {
            acc[value] = COUNTRY_NAMES[idx];
            return acc;
          }, {}),
        },
        'ui:errorMessages': {
          required: content['address-country-error'],
        },
      },
    },
  },
  schema: {
    type: 'object',
    required: ['dateOfMarriage', 'locationOfMarriage'],
    properties: {
      dateOfMarriage: currentOrPastDateSchema,
      marriedOutsideUs: checkboxSchema,
      locationOfMarriage: customAddressSchema,
    },
  },
};

/** @returns {PageSchema} */
const marriageEndDateAndLocationPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      replaceStrValues(
        content['household-previous-marriages-end-date-location-title'],
        content.your,
      ),
    ),
    dateOfSeparation: currentOrPastDateUI({
      title: content['household-previous-marriages-end-date-location-date'],
      monthSelect: false,
      validations: [validations.isAfterPreviousMarriageStartDate],
    }),
    marriageEndedOutsideUs: checkboxUI({
      title: replaceStrValues(
        content['household-previous-marriages-end-date-location-outside-us'],
        titleCase(content.my),
      ),
    }),
    locationOfSeparation: {
      city: {
        ...textUI(content['address-city']),
        'ui:errorMessages': {
          required: content['address-city-error'],
        },
      },
      state: {
        ...selectUI(content['address-state']),
        'ui:required': (formData, index) => {
          const item = formData?.spouseMarriages?.[index];
          const currentPageData = formData;
          return !(
            item?.marriageEndedOutsideUs ||
            currentPageData?.marriageEndedOutsideUs
          );
        },
        'ui:options': {
          hideIf: (formData, index) => {
            const item = formData?.spouseMarriages?.[index];
            const currentPageData = formData;
            return (
              item?.marriageEndedOutsideUs ||
              currentPageData?.marriageEndedOutsideUs
            );
          },
          labels: STATE_VALUES.reduce((acc, value, idx) => {
            acc[value] = STATE_NAMES[idx];
            return acc;
          }, {}),
        },
        'ui:errorMessages': {
          required: content['address-state-error'],
        },
      },
      otherCountry: {
        ...selectUI(content['address-country']),
        'ui:required': (formData, index) => {
          const item = formData?.spouseMarriages?.[index];
          const currentPageData = formData;
          return (
            item?.marriageEndedOutsideUs ||
            currentPageData?.marriageEndedOutsideUs
          );
        },
        'ui:options': {
          hideIf: (formData, index) => {
            const item = formData?.spouseMarriages?.[index];
            const currentPageData = formData;
            return !(
              item?.marriageEndedOutsideUs ||
              currentPageData?.marriageEndedOutsideUs
            );
          },
          labels: COUNTRY_VALUES.reduce((acc, value, idx) => {
            acc[value] = COUNTRY_NAMES[idx];
            return acc;
          }, {}),
        },
        'ui:errorMessages': {
          required: content['address-country-error'],
        },
      },
    },
  },
  schema: {
    type: 'object',
    required: ['dateOfSeparation', 'locationOfSeparation'],
    properties: {
      dateOfSeparation: currentOrPastDateSchema,
      marriageEndedOutsideUs: checkboxSchema,
      locationOfSeparation: customAddressSchema,
    },
  },
};

/** @returns {PageSchema} */
const marriageEndPage = {
  uiSchema: {
    reasonForSeparation: radioUI({
      title: content['household-previous-marriages-separation-title'],
      labels: previousMarriageEndOptions,
      labelHeaderLevel: 3,
    }),
    separationExplanation: textUI({
      title: content['household-previous-marriages-separation-explanation'],
      expandUnder: 'reasonForSeparation',
      expandUnderCondition: field => field === 'OTHER',
      required: (formData, index) => {
        const item = formData?.spouseMarriages?.[index];
        const currentPageData = formData;
        return (
          item?.reasonForSeparation === 'OTHER' ||
          currentPageData?.reasonForSeparation === 'OTHER'
        );
      },
      errorMessages: {
        required:
          content['household-previous-marriages-separation-explanation-error'],
      },
    }),
  },
  schema: {
    type: 'object',
    required: ['reasonForSeparation'],
    properties: {
      reasonForSeparation: radioSchema(Object.keys(previousMarriageEndOptions)),
      separationExplanation: customTextSchema,
    },
  },
};

export const previousMarriagesPages = arrayBuilderPages(
  options,
  pageBuilder => ({
    previousMarriagesIntro: pageBuilder.introPage({
      title: content['household-your-previous-marriages-intro-title'],
      path: 'household/previous-marriage-intro',
      depends: formData => showClaimantMarriagePage(formData),
      uiSchema: introPage.uiSchema,
      schema: introPage.schema,
    }),
    previousMarriagesSummary: pageBuilder.summaryPage({
      title: content['household-your-previous-marriages-summary-add'],
      path: 'household/previous-marriage',
      depends: formData => showClaimantMarriagePage(formData),
      uiSchema: summaryPage.uiSchema,
      schema: summaryPage.schema,
    }),
    previousMarriageItemPage: pageBuilder.itemPage({
      title: content['household-your-previous-marriages-name-title'],
      path: 'household/previous-marriage/:index/name',
      depends: formData => showClaimantMarriagePage(formData),
      uiSchema: previousMarriageItemPage.uiSchema,
      schema: previousMarriageItemPage.schema,
    }),
    previousMarriageDateAndLocationPage: pageBuilder.itemPage({
      title: replaceStrValues(
        content['household-previous-marriages-date-location-title'],
        content.you,
      ),
      path: 'household/previous-marriage/:index/date-and-location',
      depends: formData => showClaimantMarriagePage(formData),
      uiSchema: marriageDateAndLocationPage.uiSchema,
      schema: marriageDateAndLocationPage.schema,
    }),
    previousMarriageEndPage: pageBuilder.itemPage({
      title: content['household-previous-marriages-separation-title'],
      path: 'household/previous-marriage/:index/end',
      depends: formData => showClaimantMarriagePage(formData),
      uiSchema: marriageEndPage.uiSchema,
      schema: marriageEndPage.schema,
    }),
    previousMarriageEndDateAndLocationPage: pageBuilder.itemPage({
      title: replaceStrValues(
        content['household-previous-marriages-end-date-location-title'],
        content.your,
      ),
      path: 'household/previous-marriage/:index/end-date-and-location',
      depends: formData => showClaimantMarriagePage(formData),
      uiSchema: marriageEndDateAndLocationPage.uiSchema,
      schema: marriageEndDateAndLocationPage.schema,
    }),
  }),
);
